import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

/* Payments report PDF (A4 landscape).
   Standard PDF fonts cannot draw the ₹ sign, so amounts are printed as "Rs. ". */

const money = (n) =>
  'Rs. ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Standard fonts only support Latin-1 — replace anything else so pdf-lib doesn't throw.
const clean = (s) => String(s ?? '').replace(/[^\x20-\x7E\u00A0-\u00FF]/g, '?');

const fit = (text, font, size, maxW) => {
  let t = clean(text);
  if (font.widthOfTextAtSize(t, size) <= maxW) return t;
  while (t.length > 1 && font.widthOfTextAtSize(t + '...', size) > maxW) t = t.slice(0, -1);
  return t + '...';
};

const BLACK = rgb(0.07, 0.07, 0.07);
const GRAY  = rgb(0.42, 0.45, 0.5);
const LINE  = rgb(0.88, 0.9, 0.92);
const HEAD  = rgb(0.95, 0.96, 0.97);
const ZEBRA = rgb(0.985, 0.985, 0.99);
const TOTAL = rgb(0.93, 0.95, 1);
const GREEN = rgb(0.09, 0.64, 0.29);
const RED   = rgb(0.86, 0.15, 0.15);

/**
 * rows:   [{ orderNumber, date, company, customer, dueDate, grand, received, balance, status }]
 * totals: { orderCount, grand, received, balance }   (covers ALL rows, not just this list's page)
 * meta:   { filterLabel, generatedOn }
 * Returns Uint8Array (PDF bytes).
 */
export async function generatePaymentsPDF({ rows, totals, filterLabel, generatedOn }) {
  const doc   = await PDFDocument.create();
  const font  = await doc.embedFont(StandardFonts.Helvetica);
  const bold  = await doc.embedFont(StandardFonts.HelveticaBold);

  const PW = 842, PH = 595, M = 28;
  const ROW_H = 19, HEAD_H = 22, FS = 8.5;

  const cols = [
    { key: 'orderNumber', label: 'Order #',     w: 72 },
    { key: 'date',        label: 'Date',        w: 70 },
    { key: 'customer',    label: 'Customer',    w: 236 },
    { key: 'dueDate',     label: 'Due Date',    w: 70 },
    { key: 'grand',       label: 'Grand Total', w: 92, num: true },
    { key: 'received',    label: 'Received',    w: 92, num: true },
    { key: 'balance',     label: 'Balance',     w: 92, num: true },
    { key: 'status',      label: 'Status',      w: 62 },
  ];
  let x0 = M;
  cols.forEach((c) => { c.x = x0; x0 += c.w; });
  const tableW = x0 - M;

  let page, y;

  const drawTableHead = () => {
    page.drawRectangle({ x: M, y: y - HEAD_H, width: tableW, height: HEAD_H, color: HEAD });
    cols.forEach((c) => {
      const t = c.label;
      const tw = bold.widthOfTextAtSize(t, 8);
      const tx = c.num ? c.x + c.w - 6 - tw : c.x + 6;
      page.drawText(t, { x: tx, y: y - 14, size: 8, font: bold, color: GRAY });
    });
    y -= HEAD_H;
  };

  const newPage = (first) => {
    page = doc.addPage([PW, PH]);
    y = PH - M;
    if (first) {
      page.drawText('Payments Report', { x: M, y: y - 14, size: 16, font: bold, color: BLACK });
      y -= 22;
      page.drawText(fit(filterLabel || 'All orders', font, 9, tableW), { x: M, y: y - 12, size: 9, font, color: GRAY });
      y -= 14;
      page.drawText(`Generated ${clean(generatedOn)}  |  ${totals.orderCount} orders`, { x: M, y: y - 12, size: 9, font, color: GRAY });
      y -= 26;
    }
    drawTableHead();
  };

  const cellText = (c, value, f, color) => {
    const t = fit(value, f, FS, c.w - 12);
    const tw = f.widthOfTextAtSize(t, FS);
    const tx = c.num ? c.x + c.w - 6 - tw : c.x + 6;
    return { t, tx, color: color || BLACK, f };
  };

  newPage(true);

  rows.forEach((r, i) => {
    if (y - ROW_H < M + 34) newPage(false);
    if (i % 2 === 1) page.drawRectangle({ x: M, y: y - ROW_H, width: tableW, height: ROW_H, color: ZEBRA });

    const customer = r.company ? `${r.company} - ${r.customer}` : r.customer;
    const vals = {
      orderNumber: r.orderNumber, date: r.date, customer, dueDate: r.dueDate || '-',
      grand: money(r.grand), received: money(r.received),
      balance: r.balance > 0 ? money(r.balance) : '-', status: r.status,
    };
    cols.forEach((c) => {
      const color = c.key === 'received' ? GREEN : c.key === 'balance' && r.balance > 0 ? RED : BLACK;
      const ct = cellText(c, vals[c.key], c.key === 'orderNumber' ? bold : font, color);
      page.drawText(ct.t, { x: ct.tx, y: y - 13, size: FS, font: ct.f, color: ct.color });
    });
    page.drawLine({ start: { x: M, y: y - ROW_H }, end: { x: M + tableW, y: y - ROW_H }, thickness: 0.5, color: LINE });
    y -= ROW_H;
  });

  // ── Totals row ──
  if (y - 26 < M + 20) newPage(false);
  y -= 6;
  page.drawRectangle({ x: M, y: y - 24, width: tableW, height: 24, color: TOTAL });
  page.drawText(`TOTAL (${totals.orderCount} orders)`, { x: cols[0].x + 6, y: y - 16, size: 9, font: bold, color: BLACK });
  [['grand', totals.grand, BLACK], ['received', totals.received, GREEN], ['balance', totals.balance, RED]].forEach(([key, val, color]) => {
    const c = cols.find((cc) => cc.key === key);
    const t = money(val);
    const tw = bold.widthOfTextAtSize(t, 9);
    page.drawText(t, { x: c.x + c.w - 6 - tw, y: y - 16, size: 9, font: bold, color });
  });

  // ── Page numbers ──
  const pages = doc.getPages();
  pages.forEach((p, i) => {
    const t = `Page ${i + 1} of ${pages.length}`;
    p.drawText(t, { x: PW / 2 - font.widthOfTextAtSize(t, 8) / 2, y: 14, size: 8, font, color: GRAY });
  });

  return doc.save();
}
